import Ajv from 'ajv'
import type { ErrorObject, ValidateFunction } from 'ajv'
import addFormats from 'ajv-formats'
import config from '@/utils/appConfig'

/**
 * Request-payload validation against the JSON Schemas the backend validates with.
 *
 * Schemas come from the `gl-contracts` submodule (`@contracts/api/**`) and are the same
 * files the NestJS services compile — so a mismatch here is a real mismatch, not a guess.
 *
 * Only `*.json` schemas may be imported. The `.ts` ones (users/login, users/register,
 * users/updatePassword, the `*.api-schema.ts` aggregators) reach for `process.env` at import
 * time through gl-utils' `doEnv()` and are server-side only.
 *
 * @example
 * import { validatePayload } from '@/utils/validate-payload'
 * import schema from '@contracts/api/dashboard/riskBucketsAddress.request.schema.json'
 *
 * apiService.get('dashboard/risk-buckets/address', {
 *   params: validatePayload(schema, params, 'dashboard/risk-buckets/address'),
 * })
 */

export type ValidationMode = 'off' | 'warn' | 'strict'

export type JsonSchema = Record<string, unknown>

export interface PayloadValidationError extends Error {
  status: number
  errors: ErrorObject[]
}

/**
 * Validation is observation, never transformation: no type coercion, no stripping of extra
 * fields, no schema defaults. The payload that goes out is the payload that came in, so
 * turning the flag on can never change what the backend receives.
 */
function createAjv(): Ajv {
  // `strict: false` — contracts carry custom keywords (`$ts`, `bsonType`) that ajv must ignore.
  const ajv = new Ajv({ allErrors: true, strict: false })

  addFormats(ajv)

  return ajv
}

let ajv: Ajv | null = null

const validators = new Map<JsonSchema, ValidateFunction>()

/** Schemas ajv refused to compile — reported once, then skipped. */
const uncompilable = new Set<JsonSchema>()

/** Warn once per (label, message) pair — dashboards refetch on a timer. */
const warned = new Set<string>()

const MODES: ValidationMode[] = ['off', 'warn', 'strict']

let unknownModeReported = false

/**
 * Anything unrecognised — an empty value, `false`, a typo — falls back to `off` rather than
 * being rejected. appConfig is imported app-wide, so a schema-level `enum` here would turn a
 * misspelled kill-switch into a boot failure for the whole app.
 */
function getMode(): ValidationMode {
  const mode = config.VUE_APP_PAYLOAD_VALIDATION as string | undefined

  if (MODES.includes(mode as ValidationMode)) {
    return mode as ValidationMode
  }

  if (mode !== undefined && mode !== '' && !unknownModeReported) {
    unknownModeReported = true
    console.warn(
      `[contract] VUE_APP_PAYLOAD_VALIDATION=${JSON.stringify(mode)} is not one of ` +
        `${MODES.join(' | ')} — payload validation is off.`,
    )
  }

  return 'off'
}

function getValidator(schema: JsonSchema): ValidateFunction {
  let validate = validators.get(schema)

  if (!validate) {
    ajv = ajv ?? createAjv()
    validate = ajv.compile(schema)
    validators.set(schema, validate)
  }

  return validate
}

function toError(message: string, errors: ErrorObject[]): PayloadValidationError {
  const error = new Error(message) as PayloadValidationError

  error.name = 'PayloadValidationError'
  error.status = 400
  error.errors = errors

  return error
}

/**
 * Validate `payload` against a contract schema and return it unchanged.
 *
 * Behaviour is driven by `VUE_APP_PAYLOAD_VALIDATION`:
 * - `off`    — returns immediately, ajv is never compiled;
 * - `warn`   — logs a single `console.warn` per distinct problem, request goes out as-is;
 * - `strict` — throws a `PayloadValidationError` (`status: 400`, `errors[]`).
 *
 * Unrecognised values fall back to `off`. A schema that fails to compile is reported once and
 * then skipped, except in `strict` where the compile error is rethrown.
 *
 * @param schema  a `*.request.schema.json` imported from `@contracts`
 * @param payload the object about to be sent
 * @param label   endpoint path, used to make the warning readable
 */
export function validatePayload<T>(schema: JsonSchema, payload: T, label = 'payload'): T {
  const mode = getMode()

  if (mode === 'off') {
    return payload
  }

  if (mode !== 'strict' && uncompilable.has(schema)) {
    return payload
  }

  let validate: ValidateFunction

  try {
    validate = getValidator(schema)
  } catch (error) {
    // A contract ajv cannot compile — one whose `$ref` points at another schema file, say —
    // must not take the request down with it. `warn` promises the request goes out either way.
    if (mode === 'strict') {
      throw error
    }

    if (!uncompilable.has(schema)) {
      uncompilable.add(schema)
      console.error(`[contract] ${label}: schema failed to compile, not validated`, error)
    }

    return payload
  }

  if (validate(payload)) {
    return payload
  }

  const errors = validate.errors ?? []
  const message = `${label}: ${(ajv as Ajv).errorsText(errors, { dataVar: 'payload' })}`

  if (mode === 'strict') {
    throw toError(message, errors)
  }

  if (!warned.has(message)) {
    warned.add(message)
    console.warn(`[contract] ${message}`, { payload, errors })
  }

  return payload
}

/** Test seam — drops the compiled-validator cache and the warn-once ledger. */
export function resetPayloadValidation(): void {
  ajv = null
  validators.clear()
  uncompilable.clear()
  warned.clear()
  unknownModeReported = false
}
